import React, { useState, useEffect, useRef } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import SafeIcon from '../common/SafeIcon'
import Logo from './Logo'
import * as FiIcons from 'react-icons/fi'

const { FiMenu, FiX } = FiIcons

const Header = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const location = useLocation()
  const isHome = location.pathname === '/'
  
  const toggleRef = useRef(null)
  const menuRef = useRef(null)
  const lastIsOpen = useRef(isOpen)

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20)
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => {
    if (isOpen) {
      setIsOpen(false)
      toggleRef.current?.focus()
    }
    window.scrollTo(0, 0)
  }, [location])

  useEffect(() => {
    if (lastIsOpen.current === true && isOpen === false) {
      toggleRef.current?.focus()
    }
    lastIsOpen.current = isOpen
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) return

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setIsOpen(false)
        return
      }

      if (e.key === 'Tab') {
        const focusableElements = menuRef.current?.querySelectorAll(
          'a[href], button, textarea, input, select, [tabindex]:not([tabindex="-1"])'
        )
        if (!focusableElements || focusableElements.length === 0) return

        const firstElement = focusableElements[0]
        const lastElement = focusableElements[focusableElements.length - 1]

        if (e.shiftKey) {
          if (document.activeElement === firstElement) {
            e.preventDefault()
            lastElement.focus()
          }
        } else {
          if (document.activeElement === lastElement) {
            e.preventDefault()
            firstElement.focus()
          }
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    const firstFocusable = menuRef.current?.querySelector('button')
    firstFocusable?.focus()

    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isOpen])

  const navLinks = [
    { name: 'Services', path: '/services' },
    { name: 'About', path: '/about' },
    { name: 'FAQ', path: '/faq' },
    { name: 'Contact', path: '/contact' }
  ]

  const legalLinks = [
    { name: 'Privacy', path: '/privacy-policy' },
    { name: 'Terms', path: '/terms-of-service' },
    { name: 'Access', path: '/accessibility-statement' }
  ]

  const headerBg = isOpen
    ? 'bg-white border-b border-gray-100 shadow-md'
    : scrolled
      ? 'bg-white/95 backdrop-blur-md border-b border-gray-100 shadow-sm'
      : isHome
        ? 'bg-transparent'
        : 'bg-white border-b border-gray-50'

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${headerBg}`} role="banner">
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" aria-label="Main Navigation">
        <div className="flex justify-between items-center h-24">
          <Link to="/" className="z-50" aria-label="Mobile First Pro Home">
            <Logo />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-8">
            <div className="flex items-center space-x-8 mr-8 border-r border-gray-100 pr-8">
              {navLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.path}
                  className="text-xs font-black uppercase tracking-[0.2em] text-gray-900 transition-all duration-300 hover:text-orange-700 relative group py-2"
                >
                  {link.name}
                  <span className="absolute bottom-0 left-0 w-0 h-[2px] bg-orange-700 transition-all duration-300 group-hover:w-full" aria-hidden="true" />
                </Link>
              ))}
            </div>

            <div className="flex items-center space-x-6">
              {legalLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.path}
                  className="text-xs font-bold uppercase tracking-widest text-gray-600 hover:text-orange-700 transition-colors"
                >
                  {link.name}
                </Link>
              ))}
              
              <Link
                to="/contact"
                className="ml-4 px-5 py-2.5 bg-gray-900 text-white text-xs font-black uppercase tracking-[0.2em] hover:bg-orange-700 transition-all duration-300 shadow-lg shadow-gray-900/10"
                aria-label="Start a new project"
              >
                Start Project
              </Link>
            </div>
          </div>

          <button
            ref={toggleRef}
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden p-2 text-gray-900 z-50 focus:outline-none focus:ring-2 focus:ring-orange-700 rounded-md transition-colors"
            aria-expanded={isOpen}
            aria-controls="mobile-menu"
            aria-label={isOpen ? "Close menu" : "Open menu"}
          >
            <SafeIcon icon={isOpen ? FiX : FiMenu} className="text-2xl" aria-hidden="true" />
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {isOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsOpen(false)}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 lg:hidden"
              aria-hidden="true"
            />
            <motion.div
              ref={menuRef}
              id="mobile-menu"
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 180 }}
              className="fixed top-0 right-0 bottom-0 w-[85%] max-w-sm bg-white z-50 lg:hidden shadow-2xl flex flex-col p-8 md:p-12"
              role="dialog"
              aria-modal="true"
              aria-label="Mobile site menu"
            >
              <div className="flex justify-end mb-8">
                <button 
                  onClick={() => setIsOpen(false)}
                  className="p-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-orange-700 rounded-md hover:text-orange-700 transition-colors"
                  aria-label="Close menu"
                >
                  <SafeIcon icon={FiX} className="text-3xl" />
                </button>
              </div>
              
              <div className="flex flex-col space-y-4">
                {navLinks.map((link, idx) => (
                  <motion.div 
                    key={link.name} 
                    initial={{ opacity: 0, x: 20 }} 
                    animate={{ opacity: 1, x: 0 }} 
                    transition={{ delay: idx * 0.05, duration: 0.4 }}
                  >
                    <Link 
                      to={link.path} 
                      className="text-3xl font-black text-gray-900 hover:text-orange-700 transition-colors uppercase tracking-tighter block"
                    >
                      {link.name}
                    </Link>
                  </motion.div>
                ))}
                
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3, duration: 0.5 }}
                  className="pt-6"
                >
                  <Link
                    to="/contact"
                    className="block w-full text-center py-5 bg-gray-900 text-white font-black uppercase tracking-[0.2em] text-xs shadow-xl hover:bg-orange-700 transition-colors"
                  >
                    Start Project
                  </Link>
                </motion.div>
              </div>

              <div className="mt-auto pt-8 border-t border-gray-100 flex flex-col space-y-4">
                <div className="flex flex-wrap gap-x-6 gap-y-2">
                  {legalLinks.map((link) => (
                    <Link
                      key={link.name}
                      to={link.path}
                      className="text-xs font-black uppercase tracking-widest text-gray-600 hover:text-orange-700 transition-colors"
                    >
                      {link.name}
                    </Link>
                  ))}
                </div>
                <p className="text-xs text-gray-600 font-medium">
                  © {new Date().getFullYear()} Mobile First Pro Studio.
                </p>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </header>
  )
}

export default Header